import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { UserCheck as HrLogoIcon, Users, CircleCheckBig, FileClock, TriangleAlert, Table2, Search } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const KPI_STYLE = {
  'Dipendenti/Collaboratori attivi': { icon: Users, color: '#2563eb' },
  'Presenti oggi': { icon: CircleCheckBig, color: '#22c55e' },
  'Richieste da approvare': { icon: FileClock, color: '#f59e0b' },
  'Anomalie aperte': { icon: TriangleAlert, color: '#ef4444' }
};

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const PRESENZA_STATO_STYLE = {
  IN_SERVIZIO: { color: '#16a34a', borderColor: 'rgba(22,163,74,0.45)', bgcolor: 'rgba(22,163,74,0.08)' },
  IN_PAUSA: { color: '#f59e0b', borderColor: 'rgba(245,158,11,0.45)', bgcolor: 'rgba(245,158,11,0.08)' },
  IN_FERIE: { color: '#64748b', borderColor: 'rgba(100,116,139,0.45)', bgcolor: 'rgba(100,116,139,0.08)' },
  USCITO: { color: '#475569', borderColor: 'rgba(71,85,105,0.45)', bgcolor: 'rgba(71,85,105,0.08)' },
  NON_TIMBRATO: { color: '#64748b', borderColor: 'rgba(100,116,139,0.45)', bgcolor: 'rgba(100,116,139,0.08)' }
};

const StatWidgetHr = ({ title, value, delta, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%'
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.25 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {delta}
      </Typography>
    </Box>
  </Paper>
);

export default function HrRisorseUmane() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [kpiRows, setKpiRows] = useState([]);
  const [presenzeRows, setPresenzeRows] = useState([]);
  const [presenzeTotal, setPresenzeTotal] = useState(0);
  const [presenzeSearchInput, setPresenzeSearchInput] = useState('');
  const [presenzeSearch, setPresenzeSearch] = useState('');
  const [presenzePage, setPresenzePage] = useState(0);
  const [presenzeRowsPerPage, setPresenzeRowsPerPage] = useState(10);
  const [presenzeSortBy, setPresenzeSortBy] = useState('dipendente');
  const [presenzeSortDir, setPresenzeSortDir] = useState('ASC');
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setPresenzeSearch(presenzeSearchInput);
      setPresenzePage(0);
    }, 350);
    return () => window.clearTimeout(t);
  }, [presenzeSearchInput]);

  useEffect(() => {
    const loadDashboard = async () => {
      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;
      try {
        if (initialLoading) setInitialLoading(true);
        setFetching(true);
        const res = await api.get('/dashboard-personale', {
          params: {
            q: presenzeSearch.trim(),
            limit: presenzeRowsPerPage,
            offset: presenzePage * presenzeRowsPerPage,
            sortBy: presenzeSortBy,
            sortDir: presenzeSortDir
          }
        });
        if (requestSeqRef.current !== seq) return;
        setKpiRows(res.data?.kpi || []);
        setPresenzeRows(res.data?.presenzeOggi || []);
        setPresenzeTotal(Number(res.data?.presenzeMeta?.total || 0));
      } catch (error) {
        console.error('Error loading dashboard HR:', error);
        toast.error(error.response?.data?.message || 'Errore caricamento dashboard HR');
        if (requestSeqRef.current !== seq) return;
        if (initialLoading) {
          setKpiRows([]);
          setPresenzeRows([]);
          setPresenzeTotal(0);
        }
      } finally {
        if (requestSeqRef.current !== seq) return;
        setInitialLoading(false);
        setFetching(false);
      }
    };
    loadDashboard();
  }, [presenzePage, presenzeRowsPerPage, presenzeSearch, presenzeSortBy, presenzeSortDir]);

  const handlePresenzeSort = (field) => {
    if (presenzeSortBy === field) {
      setPresenzeSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setPresenzeSortBy(field);
    setPresenzeSortDir('ASC');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <HrLogoIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Dashboard HR
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Panoramica rapida del modulo Risorse Umane.
          </Typography>
        </Box>
      </Box>

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpiRows.map((item) => {
          const style = KPI_STYLE[item.label] || { icon: HrLogoIcon, color: '#6366f1' };
          return (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <StatWidgetHr
                title={item.label}
                value={item.valore}
                delta={item.delta}
                icon={style.icon}
                color={style.color}
              />
            </Grid>
          );
        })}
          </Grid>

          <StyledCard>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(145, 158, 171, 0.24)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                bgcolor: 'rgba(99,102,241,0.10)',
                color: '#6366f1',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Table2 size={16} />
            </Box>
            <Typography variant="h6">Presenze di oggi</Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Cerca dipendente o CF..."
            value={presenzeSearchInput}
            onChange={(e) => {
              setPresenzeSearchInput(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
              endAdornment: fetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null
            }}
            sx={{ minWidth: 280 }}
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.08)' }}>
              <TableRow>
                <TableCell sortDirection={presenzeSortBy === 'dipendente' ? presenzeSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={presenzeSortBy === 'dipendente'}
                    direction={presenzeSortBy === 'dipendente' ? presenzeSortDir.toLowerCase() : 'asc'}
                    onClick={() => handlePresenzeSort('dipendente')}
                  >
                    Dipendente
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={presenzeSortBy === 'prima_entrata' ? presenzeSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={presenzeSortBy === 'prima_entrata'}
                    direction={presenzeSortBy === 'prima_entrata' ? presenzeSortDir.toLowerCase() : 'asc'}
                    onClick={() => handlePresenzeSort('prima_entrata')}
                  >
                    Prima entrata
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={presenzeSortBy === 'ultima_uscita' ? presenzeSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={presenzeSortBy === 'ultima_uscita'}
                    direction={presenzeSortBy === 'ultima_uscita' ? presenzeSortDir.toLowerCase() : 'asc'}
                    onClick={() => handlePresenzeSort('ultima_uscita')}
                  >
                    Ultima uscita
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={presenzeSortBy === 'stato' ? presenzeSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={presenzeSortBy === 'stato'}
                    direction={presenzeSortBy === 'stato' ? presenzeSortDir.toLowerCase() : 'asc'}
                    onClick={() => handlePresenzeSort('stato')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={presenzeSortBy === 'anomalia' ? presenzeSortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={presenzeSortBy === 'anomalia'}
                    direction={presenzeSortBy === 'anomalia' ? presenzeSortDir.toLowerCase() : 'asc'}
                    onClick={() => handlePresenzeSort('anomalia')}
                  >
                    Anomalia
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {presenzeRows.map((row) => (
                <TableRow key={`${row.dipendente}-${row.prima_entrata}`}>
                  <TableCell>{row.dipendente}</TableCell>
                  <TableCell>{row.prima_entrata}</TableCell>
                  <TableCell>{row.ultima_uscita}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.stato}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        ...(PRESENZA_STATO_STYLE[row.stato] || {
                          color: '#64748b',
                          borderColor: 'rgba(100,116,139,0.45)',
                          bgcolor: 'rgba(100,116,139,0.08)'
                        })
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.anomalia}</TableCell>
                </TableRow>
              ))}
              {presenzeRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">Nessuna presenza disponibile oggi</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={presenzeTotal}
          page={presenzePage}
          onPageChange={(_event, newPage) => setPresenzePage(newPage)}
          rowsPerPage={presenzeRowsPerPage}
          onRowsPerPageChange={(event) => {
            setPresenzeRowsPerPage(parseInt(event.target.value, 10));
            setPresenzePage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
          </StyledCard>
        </>
      )}
    </Box>
  );
}
